import { useState } from "react";
import { MediaPicker } from "@/pages/admin/components/MediaPicker";
import { useAssetUrlMap } from "@/hooks/useAssetUrlMap";
import { isStoragePath } from "@/lib/mediaStorage";
import { btnGhost } from "@/pages/admin/components/AdminUI";

interface ShareImageFieldProps {
  value: string;
  onChange: (value: string) => void;
}

/**
 * Per-item social share image field. The value is chosen from the Media Library
 * (stored as an external URL or a Backend storage path, both resolved for the
 * live preview); the public SEO component turns the stored value into an
 * absolute og:image at render time.
 */
export default function ShareImageField({ value, onChange }: ShareImageFieldProps) {
  const [open, setOpen] = useState(false);
  const urlMap = useAssetUrlMap([value]);
  const preview = value ? (isStoragePath(value) ? urlMap[value] || "" : value) : "";

  return (
    <div>
      <span className="block text-[11px] font-semibold uppercase tracking-wider text-foreground-500 mb-2">
        Social share image
      </span>
      <div className="flex items-center gap-3">
        <div className="w-24 h-14 rounded-md overflow-hidden border border-background-300 bg-background-100 flex items-center justify-center text-foreground-400 shrink-0">
          {preview ? (
            <img
              src={preview}
              alt="Social share preview"
              className="w-full h-full object-cover object-top"
            />
          ) : (
            <i className="ri-image-2-line text-xl"></i>
          )}
        </div>
        <div className="min-w-0">
          <p className="text-[11px] text-foreground-500 mb-2">
            {value
              ? "Used as the share card when this page is posted to social or messaging apps."
              : "No share image — the page's default image is used when shared."}
          </p>
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => setOpen(true)} className={btnGhost}>
              <i className="ri-image-2-line"></i> {value ? "Change image" : "Choose from library"}
            </button>
            {value ? (
              <button type="button" onClick={() => onChange("")} className={btnGhost}>
                <i className="ri-close-line"></i> Remove
              </button>
            ) : null}
          </div>
        </div>
      </div>

      <MediaPicker
        open={open}
        onClose={() => setOpen(false)}
        onSelect={(selected) => onChange(selected)}
      />
    </div>
  );
}